import { token } from '@atlaskit/tokens';

import { carouselExtensionKey, carouselExtensionType } from './constants';
import {
	ButtonStyle,
	type CarouselParameters,
	CommonLayout,
	CornerStyle,
	type Frame,
	FRAME_FIELDS,
	NavigationStyle,
} from './types';

export const mockInitialParameters: {
	cards: never[];
	extensionTitle: string;
	isAvatarShown: boolean;
	isPublishDateShown: boolean;
	isSegmentationShown: boolean;
	size: string;
	version: string;
} = {
	size: 'medium',
	isPublishDateShown: false,
	isAvatarShown: true,
	isSegmentationShown: false,
	extensionTitle: 'Carousel',
	version: '1',
	cards: [],
};

export const mockCarouselWithOptionalValues: Frame[] = [
	{
		cardId: '1',
		title: 'Improving Pipeline Stability: Cypress Test Isolation',
		link: 'https://www.atlassian.com/continuous-delivery/software-testing',
		description: 'desc 1',
		imageSrc:
			'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1080',
		imageAltText: 'green mountain across body of water',
		backgroundColor: 'var(--ds-background-input, #FFFFFF)',
		buttonStyle: ButtonStyle.DEFAULT,
		buttonText: '',
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
		textColor: 'var(--ds-text, #172B4D)',
	},
	{
		cardId: '2',
		title: 'Pug link',
		link: 'https://pug.jira-dev.com/wiki/spaces/CFE/blog/2023/11/28/452982538475/Sweet-State+Sweeter+APIs',
		description: 'desc 2',
		imageSrc: 'some-source-2.com',
		imageAltText: 'Luminous urban glow',
		backgroundColor: 'var(--ds-background-input, #FFFFFF)',
		buttonStyle: ButtonStyle.DEFAULT,
		buttonText: '',
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
		textColor: 'var(--ds-text, #172B4D)',
	},
	{
		cardId: '3',
		title: 'Connie',
		link: 'https://www.atlassian.com/software/confluence',
		description: 'Some description text to tell the viewer a little bit about the documentation.',
		imageSrc:
			'https://images.unsplash.com/photo-1580100586938-02822d99c4a8?auto=format&fit=crop&q=80&w=1080',
		imageAltText: 'body of water near mountain during daytime',
		backgroundColor: 'var(--ds-background-input, #FFFFFF)',
		buttonStyle: ButtonStyle.DEFAULT,
		buttonText: '',
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
		textColor: 'var(--ds-text, #172B4D)',
	},
];

export const mockInitialFrameParameters: Frame = {
	[FRAME_FIELDS.CARD_ID]: 'frame-001',
	[FRAME_FIELDS.LINK]: 'https://www.atlassian.com/continuous-delivery/software-testing',
	[FRAME_FIELDS.IS_LINK_USER_EDITED]: true,
	[FRAME_FIELDS.TITLE]: 'Welcome to Our Site',
	[FRAME_FIELDS.IS_TITLE_USER_EDITED]: false,
	[FRAME_FIELDS.DESCRIPTION]: 'An introduction to what we do.',
	[FRAME_FIELDS.IS_DESCRIPTION_USER_EDITED]: true,
	[FRAME_FIELDS.IMAGE_SRC]:
		'https://images.unsplash.com/photo-1488470015861-8bb1317353ef?auto=format&fit=crop&q=80&w=1080',
	[FRAME_FIELDS.IMAGE_ALT_TEXT]: 'People collaborating in an office',
	[FRAME_FIELDS.CONFLUENCE_PAGE_ID]: '12345',
	[FRAME_FIELDS.BACKGROUND_COLOR]: token('elevation.surface.overlay'),
	[FRAME_FIELDS.TEXT_COLOR]: token('color.text'),
	[FRAME_FIELDS.LAYOUT]: CommonLayout.FRAME_ABOVE_IMAGE,
	[FRAME_FIELDS.BUTTON_TEXT]: 'mockButtonText',
	[FRAME_FIELDS.BUTTON_STYLE]: ButtonStyle.DEFAULT,
};

export const getExtensionNodeFromParams = (
	parameters: any,
): {
	content: null;
	extensionKey: string;
	extensionType: string;
	localId: string;
	parameters: any;
	type: string;
} => ({
	content: null,
	extensionKey: carouselExtensionKey,
	extensionType: carouselExtensionType,
	localId: 'local-123',
	parameters,
	type: 'extension',
});

export const mockUnsplashCarouselParameters: {
	cards: Frame[];
	cornerStyle: CornerStyle;
	extensionTitle: string;
	isAutoplayEnabled: boolean;
	navigationStyle: NavigationStyle;
	version: string;
} = {
	extensionTitle: 'Carousel',
	cards: mockCarouselWithOptionalValues,
	version: '2',
	cornerStyle: CornerStyle.ROUNDED,
	isAutoplayEnabled: false,
	navigationStyle: NavigationStyle.THUMBNAILS,
};

export const mockInitialCarouselParameters: {
	cards: {
		cardId: string;
		description: string;
		imageAltText: string;
		imagePosition: string;
		imageSrc: string;
		link: string;
		title: string;
	}[];
	extensionTitle: string;
	isAvatarShown: boolean;
	isPublishDateShown: boolean;
	isSegmentationShown: boolean;
	renderVariant: any;
	size: string;
	version: string;
} = {
	size: 'small',
	extensionTitle: 'Carousel',
	cards: [
		{
			cardId: '12345',
			link: '',
			title: '',
			description: '',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imagePosition: 'middle',
			imageAltText: 'orange petaled flowers',
		},
		{
			cardId: '123456',
			link: '',
			title: '',
			description: 'Cpslkfjlsdjfksldfjklsdjfsdkfsjlfksldflskjdfklsdjfkls lskdjfljk skfdlj',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imagePosition: 'middle',
			imageAltText: 'orange petaled flowers',
		},
		{
			cardId: '1234567',
			link: '',
			title: 'Very nice',
			description: '',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imagePosition: 'middle',
			imageAltText: 'orange petaled flowers',
		},
	],
	isPublishDateShown: false,
	isAvatarShown: false,
	isSegmentationShown: false,
	version: '1',
	renderVariant: undefined as any,
};

export const mockV2Carousel10FramesParameters: CarouselParameters = {
	extensionTitle: 'Carousel',
	cards: [
		{
			cardId: '12345',
			link: 'google.com',
			title: 'Company News',
			description: 'Visit the link below to check out latest company news',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imageAltText: 'orange petaled flowers',
			backgroundColor: '#f2f9ff',
			textColor: 'var(--ds-text)',
			layout: CommonLayout.SPLIT_IMAGE_RIGHT,
			buttonText: 'View news',
			buttonStyle: ButtonStyle.ALTERNATIVE_1,
		},
		{
			cardId: '123456',
			link: '',
			title: 'Reminder about mandatory trainings',
			description: 'Cpslkfjlsdjfksldfjklsdjfsdkfsjlfksldflskjdfklsdjfkls lskdjfljk skfdlj',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imageAltText: 'orange petaled flowers',
			backgroundColor: '#f1f1ff',
			textColor: 'var(--ds-text)',
			layout: CommonLayout.NO_IMAGE_BACKGROUND,
		},
		{
			cardId: '1234567',
			link: '',
			title: 'Very nice',
			description: '',
			imageSrc:
				'https://images.unsplash.com/photo-1580100586938-02822d99c4a8?auto=format&fit=crop&q=80&w=1080',
			imageAltText: 'orange petaled flowers',
			layout: CommonLayout.SPLIT_IMAGE_LEFT,
		},
		{
			cardId: '1234567',
			link: '',
			title: 'Very nice',
			description: '',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imageAltText: 'orange petaled flowers',
		},
		...Array.from({ length: 7 }, (_, i) => ({
			cardId: `1234567-${i}`,
			link: '',
			title: `Very nice ${i}`,
			description: '',
			imageSrc: 'https://images.unsplash.com/some-flower-pic',
			imageAltText: 'orange petaled flowers',
		})),
	],
	version: '3',
	isAutoplayEnabled: false,
	navigationStyle: NavigationStyle.PROGRESS_CIRCLES,
};

export const mockCarouselV2WithOptionalValues: Frame[] = [
	{
		cardId: '1',
		title: 'Improving Pipeline Stability: Cypress Test Isolation',
		link: 'https://www.atlassian.com/continuous-delivery/software-testing',
		description: 'desc 1',
		imageSrc:
			'https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&q=80&w=1080',
		imageAltText: 'green mountain across body of water',
		backgroundColor: 'var(--ds-surface)',
		textColor: 'var(--ds-text)',
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
	},
	{
		cardId: '2',
		title: 'Pug link',
		link: 'https://pug.jira-dev.com/wiki/spaces/CFE/blog/2023/11/28/452982538475/Sweet-State+Sweeter+APIs',
		description: 'desc 2',
		imageSrc: 'some-source-2.com',
		imageAltText: 'Luminous urban glow',
		backgroundColor: 'var(--ds-surface)',
		textColor: 'var(--ds-text)',
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
	},
	{
		cardId: '3',
		title: 'Connie',
		link: 'https://www.atlassian.com/software/confluence',
		description: 'Some description text to tell the viewer a little bit about the documentation.',
		imageSrc:
			'https://images.unsplash.com/photo-1580100586938-02822d99c4a8?auto=format&fit=crop&q=80&w=1080',
		imageAltText: 'body of water near mountain during daytime',
		backgroundColor: 'var(--ds-surface)',
		textColor: 'var(--ds-text)',
		layout: CommonLayout.SPLIT_IMAGE_RIGHT,
	},
];

export const mockExistingExtensionParameters: CarouselParameters = {
	cards: mockCarouselWithOptionalValues,
	extensionTitle: 'Carousel',
	version: '2',
	cornerStyle: CornerStyle.ROUNDED,
	isAutoplayEnabled: false,
	navigationStyle: NavigationStyle.THUMBNAILS,
};

export const mockRawNodeExtension = (): {
	attrs: {
		extensionKey: string;
		extensionType: string;
		layout: string;
		localId: string;
		parameters: CarouselParameters;
		text: null;
	};
	type: string;
} => {
	return {
		type: 'extension',
		attrs: {
			extensionType: carouselExtensionType,
			extensionKey: carouselExtensionKey,
			parameters: mockExistingExtensionParameters,
			text: null,
			layout: 'default',
			localId: 'some-localId',
		},
	};
};

export const mockRawNodeExtensionWithExistingParameters: {
	attrs: {
		extensionKey: string;
		extensionType: string;
		layout: string;
		localId: string;
		parameters: CarouselParameters;
		text: null;
	};
	type: string;
} = {
	type: 'extension',
	attrs: {
		extensionType: carouselExtensionType,
		extensionKey: carouselExtensionKey,
		parameters: mockExistingExtensionParameters,
		text: null,
		layout: 'default',
		localId: 'some-localId',
	},
};
